"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { iSupplier } from "@sdk/suppliers/supplier-model";
import type { ProductType } from "@sdk/bookings/booking-model";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { useBrands } from "@/hooks/use-brands";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { CurrencySelector } from "@/components/currency-selector";
import { SupplierDefaultLedgerLinks } from "@/components/accounting/default-ledger-links";
import { PaymentMethodBrandRestrictionFields } from "@/components/payment-methods/payment-method-brand-restriction-fields";

export const PRODUCT_TYPE_OPTIONS: ProductType[] = [
  "FLIGHT",
  "ACCOMMODATION",
  "CRUISE",
  "CAR",
  "TRANSFER",
  "ATTRACTION",
  "EXCURSION",
  "RAIL",
  "TOUR",
];

export const PRODUCT_TYPE_LABEL: Record<ProductType, string> = {
  FLIGHT: "Flight",
  ACCOMMODATION: "Accommodation",
  CRUISE: "Cruise",
  CAR: "Car",
  TRANSFER: "Transfer",
  ATTRACTION: "Attraction",
  EXCURSION: "Excursion",
  RAIL: "Rail",
  TOUR: "Tour",
};

function toProductTypes(values: string[]): ProductType[] {
  const set = new Set(PRODUCT_TYPE_OPTIONS);
  return values.filter((v): v is ProductType => set.has(v as ProductType));
}

export interface AddEditSupplierModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  supplier?: iSupplier;
  onSuccess?: () => void;
  retry: () => void;
}

export function AddEditSupplierModal({
  open,
  onOpenChange,
  mode,
  supplier,
  onSuccess,
  retry,
}: AddEditSupplierModalProps) {
  const [name, setName] = useState(supplier?.name ?? "");
  const [productTypes, setProductTypes] = useState<ProductType[]>(
    toProductTypes(supplier?.productTypes ?? [])
  );
  const [archived, setArchived] = useState(supplier?.archived ?? false);
  const [restrictByBrands, setRestrictByBrands] = useState(
    supplier?.restrictByBrands ?? false
  );
  const [allowedBrands, setAllowedBrands] = useState<string[]>(
    supplier?.allowedBrands ?? []
  );
  const { brands } = useBrands();
  const defaultBrandCurrency = useMemo(() => {
    const defaultBrand =
      brands.find((brand) => brand.isDefault) ?? brands[0];
    return defaultBrand?.homeCurrency?.trim().toUpperCase() ?? "GBP";
  }, [brands]);
  const [defaultCurrency, setDefaultCurrency] = useState(
    supplier?.defaultCurrency?.trim().toUpperCase() ?? defaultBrandCurrency
  );

  useEffect(() => {
    if (open) {
      setName(supplier?.name ?? "");
      setProductTypes(toProductTypes(supplier?.productTypes ?? []));
      setArchived(supplier?.archived ?? false);
      setRestrictByBrands(supplier?.restrictByBrands ?? false);
      setAllowedBrands(supplier?.allowedBrands ?? []);
      setDefaultCurrency(
        supplier?.defaultCurrency?.trim().toUpperCase() ?? defaultBrandCurrency
      );
    }
  }, [
    open,
    supplier?.name,
    supplier?.productTypes,
    supplier?.archived,
    supplier?.restrictByBrands,
    supplier?.allowedBrands,
    supplier?.defaultCurrency,
    defaultBrandCurrency,
  ]);

  const { doAction: createSupplier, isProcessing: isCreateProcessing } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (details: Partial<iSupplier>) =>
          SDK.suppliers.create(details),
        successMessage: "Supplier has been created",
        failureMessage: "Something went wrong",
      }),
      []
    );

  const { doAction: editSupplier, isProcessing: isEditProcessing } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (id: string, edits: Partial<iSupplier>) =>
          SDK.suppliers.editById(id, edits),
        successMessage: "Supplier has been updated",
        failureMessage: "Something went wrong",
      }),
      []
    );

  const isProcessing = isCreateProcessing || isEditProcessing;
  const title = mode === "create" ? "Add supplier" : "Edit supplier";

  const toggleProductType = useCallback((type: ProductType) => {
    setProductTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    const currency = defaultCurrency.trim().toUpperCase();
    if (mode === "create" && !currency) return;

    const brandRestrictionFields = {
      restrictByBrands,
      allowedBrands: restrictByBrands ? allowedBrands : undefined,
    };

    if (mode === "create") {
      const created = await createSupplier({
        name: trimmedName,
        productTypes: productTypes as string[],
        defaultCurrency: currency,
        ...brandRestrictionFields,
      });
      if (created) {
        retry();
        onOpenChange(false);
        onSuccess?.();
      }
    } else if (supplier?.id) {
      await editSupplier(supplier.id, {
        name: trimmedName,
        productTypes: productTypes as string[],
        ...brandRestrictionFields,
        archived,
      });
      retry();
      onOpenChange(false);
      onSuccess?.();
    }
  }, [
    name,
    productTypes,
    defaultCurrency,
    mode,
    supplier?.id,
    restrictByBrands,
    allowedBrands,
    archived,
    createSupplier,
    editSupplier,
    retry,
    onOpenChange,
    onSuccess,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <DialogHeader className="shrink-0 border-b px-6 py-4 text-left">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="supplier-name">Name</Label>
            <Input
              id="supplier-name"
              placeholder="Supplier name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            />
          </div>
          {mode === "create" ? (
            <div className="grid gap-2">
              <CurrencySelector
                id="supplier-default-currency"
                label="Default Accounting Currency"
                value={defaultCurrency}
                onValueChange={setDefaultCurrency}
                extraCurrencies={[defaultBrandCurrency]}
                placeholder="Select currency"
              />
              <p className="text-xs text-muted-foreground">
                Used for expense and accounts payable ledgers. Cannot be changed
                after creation.
              </p>
            </div>
          ) : (
            <div className="space-y-2 rounded-md border border-border bg-muted/30 p-4">
              <p className="text-sm font-medium">Default Accounting Currency</p>
              <p className="font-mono text-sm tabular-nums">
                {supplier?.defaultCurrency ?? "—"}
              </p>
            </div>
          )}
          <div className="grid gap-2">
            <Label>Product types</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  title={
                    productTypes.length > 0
                      ? productTypes.map((t) => PRODUCT_TYPE_LABEL[t]).join(", ")
                      : undefined
                  }
                  className={cn(
                    "w-full justify-between font-normal min-h-10",
                    !productTypes.length && "text-muted-foreground"
                  )}
                >
                  <span className="truncate">
                    {productTypes.length > 0
                      ? `${productTypes.length} selected: ${productTypes.map((t) => PRODUCT_TYPE_LABEL[t]).join(", ")}`
                      : "Select product types..."}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-2" align="start">
                <div className="flex flex-col gap-1.5 max-h-[280px] overflow-y-auto">
                  {PRODUCT_TYPE_OPTIONS.map((type) => (
                    <label
                      key={type}
                      className="flex items-center gap-2 rounded-sm py-2 px-2 cursor-pointer hover:bg-muted/50"
                    >
                      <Checkbox
                        checked={productTypes.includes(type)}
                        onCheckedChange={() => toggleProductType(type)}
                      />
                      <span className="text-sm">
                        {PRODUCT_TYPE_LABEL[type]}
                      </span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <PaymentMethodBrandRestrictionFields
            restrictByBrands={restrictByBrands}
            allowedBrands={allowedBrands}
            onRestrictByBrandsChange={setRestrictByBrands}
            onAllowedBrandsChange={setAllowedBrands}
            description="When enabled, this supplier only appears in supplier selectors for the selected brands."
          />

          {mode === "edit" ? (
            <>
              {supplier?.defaultLedgerAccounts ? (
                <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
                  <Label className="text-sm">Accounting ledgers</Label>
                  <p className="text-xs text-muted-foreground">
                    Auto-provisioned when this supplier was created. Used for
                    bill accrual and supplier payment journals.
                  </p>
                  <SupplierDefaultLedgerLinks
                    defaultLedgerAccounts={supplier.defaultLedgerAccounts}
                  />
                </div>
              ) : null}
              <div className="flex items-center gap-2 rounded-lg border p-3">
              <Checkbox
                id="supplier-archived"
                checked={archived}
                onCheckedChange={(checked) => setArchived(checked === true)}
              />
              <div className="space-y-1">
                <Label htmlFor="supplier-archived" className="text-sm">
                  Archived
                </Label>
                <p className="text-xs text-muted-foreground">
                  Archived suppliers are hidden from new product selections but
                  remain on existing products.
                </p>
              </div>
            </div>
            </>
          ) : null}
        </div>
        </div>
        <DialogFooter className="shrink-0 border-t px-6 py-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isProcessing || !name.trim() || (mode === "create" && !defaultCurrency.trim())}
          >
            {isProcessing ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
